import * as React from 'react';

// Utils
import { ListingReview } from '@gql';
import { capitalizeAllWords } from '@utils/string-helpers';
import { useReviews } from './reviews-context';

// UI
import { Button } from '@components/button';
import { Typography } from '@components/typography';
import { ReviewStars } from './review-stars';
import { ExpandableText } from '@components/expandable-text';

type Props = {
  reviews: Pick<ListingReview, 'date' | 'authorName' | 'body' | 'rating'>[];
  showAllEnabled: boolean;
  toggleShowAll: () => void;
};

export const ReviewsSection: React.FC<Props> = ({ reviews }) => {
  const { activateReviewsModal, deactivateReviewsModal, reviewsModalActive } =
    useReviews();

  return (
    <div className="-mb-2">
      <div className="grid gap-8 grid-cols-1">
        {reviews.map((review, index) => (
          <div key={index} className="flex flex-col space-y-2">
            <div className="flex flex-row justify-start items-center space-x-2">
              {review.rating && (
                <div className="flex flex-row items-center space-x-1 font-semibold text-sm">
                  <ReviewStars rating={review.rating} />

                  {review.date && (
                    <>
                      <div className="px-1">&bull;</div>
                      <Typography variantM="subtitle2" component="span">
                        {new Date(review.date).toLocaleDateString()}
                      </Typography>
                    </>
                  )}
                </div>
              )}
            </div>

            <div>
              {review.body && (
                <ExpandableText
                  text={review.body}
                  maxLines={6}
                  maxLength={350}
                  typographyVariant="body2"
                  className="text-primary-main"
                  buttonColor="primary"
                  buttonVariant="text"
                  buttonClassName="underline"
                />
              )}
            </div>

            {review.authorName && (
              <Typography component="p" variant="body2">
                - {capitalizeAllWords(review.authorName)}
              </Typography>
            )}
          </div>
        ))}
      </div>

      <div className="flex flex-row justify-center pt-8 pb-4">
        <Button
          size="lg"
          onClick={
            reviewsModalActive ? deactivateReviewsModal : activateReviewsModal
          }
          variant="outlined"
          color="primary"
        >
          <div className="text-xs">Show All Reviews</div>
        </Button>
      </div>
    </div>
  );
};
